if (!customElements.get('story-navigator')) {
  class StoryNavigator extends HTMLElement {
    connectedCallback() {
      this.items = this.querySelector('[data-story-items]');
      this.sidenav = this.querySelector('.story__sidenav');
      this.toggle = this.querySelector('[data-story-toggle]');

      this._handleMediaChange = this._handleMediaChange.bind(this);
      this._handleToggleClick = this._handleToggleClick.bind(this);
      this._handleSidenavClick = this._handleSidenavClick.bind(this);
      this._updateCollapsedHeight = this._updateCollapsedHeight.bind(this);

      if (this.sidenav && this.items) {
        this._links = Array.from(this.sidenav.querySelectorAll('[data-story-year-link]'));
        this._mediaQuery = window.matchMedia('(min-width: 1024px)');
        this._mediaQuery.addEventListener('change', this._handleMediaChange);
        this._handleMediaChange(this._mediaQuery);
        this.sidenav.addEventListener('click', this._handleSidenavClick);
      }

      if (this.toggle) {
        this.toggle.addEventListener('click', this._handleToggleClick);
      }

      if (this.toggle && this.items) {
        this._debouncedUpdateCollapsedHeight = theme.utils.debounce(this._updateCollapsedHeight, 100);
        this._updateCollapsedHeight();
        window.addEventListener('resize', this._debouncedUpdateCollapsedHeight);
      }
    }

    disconnectedCallback() {
      this._observer?.disconnect();
      this._mediaQuery?.removeEventListener('change', this._handleMediaChange);
      this.toggle?.removeEventListener('click', this._handleToggleClick);
      this.sidenav?.removeEventListener('click', this._handleSidenavClick);
      if (this._debouncedUpdateCollapsedHeight) {
        window.removeEventListener('resize', this._debouncedUpdateCollapsedHeight);
      }
    }

    _updateCollapsedHeight() {
      const visibleItems = Array.from(this.items.children).filter((item) => !item.hasAttribute('data-story-truncated'));
      if (!visibleItems.length) return;

      const lastVisible = visibleItems[visibleItems.length - 1];
      const gap = parseFloat(getComputedStyle(this.items).rowGap) || 0;

      const height = visibleItems.reduce((total, item, index) => {
        const itemHeight = item === lastVisible ? item.offsetHeight * 0.85 : item.offsetHeight;
        return total + itemHeight + (index > 0 ? gap : 0);
      }, 0);

      this.items.style.setProperty('--story-items-collapsed-height', `${height}px`);
    }

    _handleSidenavClick(event) {
      const link = event.target.closest('[data-story-year-link]');
      if (!link) return;

      const target = document.getElementById(link.getAttribute('href').slice(1));
      if (!target) return;

      event.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      history.pushState(null, '', link.getAttribute('href'));
    }

    _handleMediaChange(event) {
      if (event.matches) {
        this._observeItems();
      } else {
        this._observer?.disconnect();
        this._observer = null;
        this._links.forEach((link) => link.removeAttribute('aria-current'));
      }
    }

    _observeItems() {
      if (this._observer || !this._links.length) return;

      this._observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const link = this.sidenav.querySelector(`[href="#${entry.target.id}"]`);
            if (link) this._setActiveLink(link);
          });
        },
        { rootMargin: '-40% 0px -40% 0px', threshold: 0 }
      );

      Array.from(this.items.children).forEach((item) => {
        if (item.id) this._observer.observe(item);
      });
    }

    _setActiveLink(link) {
      this._links.forEach((current) => current.removeAttribute('aria-current'));
      link.setAttribute('aria-current', 'true');
    }

    _handleToggleClick() {
      const expanded = this.hasAttribute('data-expanded');

      if (expanded) {
        this.removeAttribute('data-expanded');
      } else {
        this.setAttribute('data-expanded', 'true');
      }

      this.toggle.setAttribute('aria-expanded', String(!expanded));

      const label = this.toggle.querySelector('.button__label');
      const nextText = expanded ? this.toggle.dataset.showMoreLabel : this.toggle.dataset.showLessLabel;
      if (label && nextText) label.textContent = nextText;
    }
  }

  customElements.define('story-navigator', StoryNavigator);
}
